import { useCallback } from 'react';

import { createStore, useAtomValue, useSetAtom, useStore, type PrimitiveAtom } from 'jotai';

type JotaiStore = ReturnType<typeof createStore>;

const controllerRegistry = new WeakMap<JotaiStore, Map<string, AbortController>>();

const getControllerMap = (store: JotaiStore) => {
  let controllerMap = controllerRegistry.get(store);

  if (controllerMap == null) {
    controllerMap = new Map<string, AbortController>();
    controllerRegistry.set(store, controllerMap);
  }

  return controllerMap;
};

interface UseAbortableRequestOptions<TVariables, TData> {
  pendingAtom: PrimitiveAtom<boolean>;
  request: (variables: TVariables, signal: AbortSignal) => Promise<TData>;
  requestKey: string;
}

export const useAbortableRequest = <TVariables, TData>({
  pendingAtom,
  request,
  requestKey,
}: UseAbortableRequestOptions<TVariables, TData>) => {
  const store = useStore();
  const isPending = useAtomValue(pendingAtom);
  const setPending = useSetAtom(pendingAtom);

  const abort = useCallback(() => {
    const controllerMap = getControllerMap(store);

    controllerMap.get(requestKey)?.abort();
    controllerMap.delete(requestKey);
    setPending(false);
  }, [requestKey, setPending, store]);

  const execute = useCallback(
    async (variables: TVariables) => {
      const controllerMap = getControllerMap(store);

      controllerMap.get(requestKey)?.abort();

      const nextController = new AbortController();
      controllerMap.set(requestKey, nextController);
      setPending(true);

      try {
        return await request(variables, nextController.signal);
      } finally {
        if (controllerMap.get(requestKey) === nextController) {
          controllerMap.delete(requestKey);
        }

        setPending(false);
      }
    },
    [request, requestKey, setPending, store],
  );

  return {
    abort,
    execute,
    isPending,
  };
};
